import React, { Component } from "react";
import { localize } from "src/utils/l10n";
import { Line } from "react-chartjs-2";
import {
    Chart as ChartJS,
    RadialLinearScale,
    PointElement,
    LineElement,
    Filler,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale,
    ChartOptions,
} from "chart.js";
import {
    Callout,
    DatePicker,
    DayOfWeek,
    DefaultButton,
    Dropdown,
    PrimaryButton,
} from "@fluentui/react";
import Label from "src/components/Label/Label";
import "./DetailsView.scss";
import { RouteComponentProps } from "react-router-dom";
import api from "src/api/SpintrApi";
import moment from "moment";
import Loader from "src/components/Loader/Loader";
import UnstyledButton from "src/components/UnstyledButton/UnstyledButton";

import { listActionMenuWidth } from "src/utils/style";
import { connect } from "react-redux";
import annotationPlugin from "chartjs-plugin-annotation";
import { IApplicationState } from "src/reducer";
import Visage2Icon from "../Visage2Icon/Visage2Icon";
import { AxiosError, AxiosResponse } from "axios";
import Page from "../Page/Page";
import Notifications from "../Notifications/Notifications";
import VectorSelector from "../ProjectSelector/VectorSelector";
import SpintrList from "../SpintrList/SpintrList";
import { capitalizeFirstLetter } from "src/utils";
import gradient from 'chartjs-plugin-gradient';
import LabelBox from "../LabelBox/LabelBox";
import { ActionMenu } from "../ActionMenu";
import StatusMessageWithIcon from "../StatusMessageWithIcon/StatusMessageWithIcon";
import FormPopup from "../FormPopup/FormPopup";
import ActionItemForm from "../ActionItemForm/ActionItemForm";
import { SpintrTypes } from "src/typings";
import SpintrUser from "../SpintrUser/SpintrUser";
import { setConfirmPopup } from "src/actions/popup";
import * as Style from 'src/utils/style';
import onFormClose from "src/utils/onFormClose";
import GoalForm from "../GoalForm/GoalForm";
import FilterButton from "../FilterButton/FilterButton";
import FormSection from "../FormSection/FormSection";
import FormControl from "../FormControl/FormControl";
import formatDatePickerDate from "src/utils/formatDatePickerDate";
import getDatePickerLanguageStrings from "src/utils/getDatePickerLanguageStrings";
import ChartFooterButtons from "../ChartFooterButtons/ChartFooterButtons";
import ExportDataPopup from "../ExportDataPopup/ExportDataPopup";
import { compare } from "src/utils/compare";
import { getRequestErrors } from "src/utils/getRequestErrors";
import ProjectSelector from "../ProjectSelector/ProjectSelector";
import { IPopupFormState } from "src/reducers/ui";
import ProjectForm from "../ProjectForm/ProjectForm";

ChartJS.register(
    RadialLinearScale,
    PointElement,
    LineElement,
    Filler,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale,
    annotationPlugin,
    gradient
);

interface IRouteParams {
    vectorId: string;
}

interface IProps extends RouteComponentProps<IRouteParams> {
    hasCustomBodyFont: boolean;
    instanceColor: string;
    pro?: boolean;
    isAdmin: boolean;
    dispatch?: any;
    activeProjectId: number;
    activeVectorId: number;
    projects?: any[];
}

interface IState {
    data?: any;
    isLoading: boolean;
    targetX: number;
    targetY: number;
    selectedActionItemId: number;
    showCommentsCalloutForSurveyId: number;
    targetGroups?: any[];
    targetGroupId?: number;
    surveys?: any[];
    surveyId?: number;
    questionId?: number;
    displayExportPopup: boolean;
    errors: string[];
    popupFormState?: IPopupFormState;
    goalPopupFormState?: IPopupFormState;
    actionPopupFormState?: IPopupFormState;
}

class DetailsView extends Component<IProps, IState> {
    private surveysListRef = React.createRef<SpintrTypes.WrappedComponentType<typeof SpintrList>>();
    private actionItemsListRef = React.createRef<SpintrTypes.WrappedComponentType<typeof SpintrList>>();
    private questionsListRef = React.createRef<SpintrTypes.WrappedComponentType<typeof SpintrList>>();

    constructor(props) {
        super(props);

        this.state = {
            isLoading: true,
            targetX: 0,
            targetY: 0,
            selectedActionItemId: 0,
            showCommentsCalloutForSurveyId: 0,
            displayExportPopup: false,
            errors: []
        };
    }

    public componentDidMount() {
        this.fetch();
        this.fetchTargetGroups();
        this.fetchSurveys();
    }

    private fetch = async () => {
        if (!this.props.activeVectorId) {
            this.setState({
                isLoading: false
            });

            return;
        }

        api.get(`/api/v1/surveys/radar/details/${this.props.activeVectorId}`, {
            params: {
                targetGroupId: this.state.targetGroupId || undefined,
                questionId: this.state.questionId || undefined,
                surveyId: this.state.surveyId || undefined
            }
        }).then((response: AxiosResponse) => {
            const surveys = response.data.surveys.map(x => {
                return {
                    ...x,
                    color: this.stringToColor(x.createdDate)
                }
            });

            this.setState({
                isLoading: false,
                data: {
                    ...response.data,
                    surveys,
                    actionItems: response.data.actionItems.map(x => {
                        return {
                            ...x,
                            dueDate: new Date(x.dueDate),
                            vectorId: this.props.activeVectorId,
                            assigneeName: x.assignee ? x.assignee.name : ""
                        }
                    })
                },
                errors: []
            }, () => {
                if (this.surveysListRef.current) {
                    //@ts-ignore
                    this.surveysListRef.current.reFetch();
                }

                if (this.actionItemsListRef.current) {
                    //@ts-ignore
                    this.actionItemsListRef.current.reFetch();
                }

                if (this.questionsListRef.current) {
                    //@ts-ignore
                    this.questionsListRef.current.reFetch();
                }
            });
        }).catch((error: AxiosError) => {
            this.setState({
                errors: getRequestErrors(error),
                isLoading: false
            });
        });
    };

    private fetchTargetGroups = () => {
        api.get("/api/v1/targetgroups", {params: {take: 9999, includeEmpty: false}}).then((response) => {
            this.setState({
                targetGroups: response.data.items
            });
        });
    }

    private fetchSurveys = () => {
        api.get("/api/v1/surveys", {
            params: {
                vectorId: this.props.activeVectorId
            },
        }).then((response: AxiosResponse) => {
            this.setState({
                surveys: response.data.items
            });
        });
    }

    componentDidUpdate(prevProps: Readonly<IProps>, prevState: Readonly<IState>, snapshot?: any): void {
        if (this.props.activeVectorId !== prevProps.activeVectorId) {
            this.setState((prevState) => ({
                questionId: this.props.activeProjectId !== prevProps.activeProjectId ? 0 : prevState.questionId,
                surveyId: this.props.activeProjectId !== prevProps.activeProjectId ? 0 : prevState.surveyId,
                targetGroupId: this.props.activeProjectId !== prevProps.activeProjectId ? 0 : prevState.targetGroupId
            }), this.fetch)
        }
    }
    
    render() {
        return (
            <Page renderHeader={this.renderHeader.bind(this)} noContentStyle pageClassName="DetailsView">
                {this.renderContent()}
                {this.state.displayExportPopup && (
                    <ExportDataPopup
                        projectId={this.props.activeProjectId}
                        targetGroupId={this.state.targetGroupId}
                        onClose={() => {
                            this.setState({
                                displayExportPopup: false
                            });
                        }}
                    />
                )}
                {this.renderFormPopup()}
            </Page>
        )
    }

    renderHeader() {
        const activeProject = this.props.projects.find(x => x.id === this.props.activeProjectId);

        return (
            <div className="header-inner">
                <div className="header-left">
                    {activeProject?.template === 2 && <ProjectSelector />}
                    {activeProject?.template !== 2 && <VectorSelector />}
                </div>
                <div className="header-right">
                    {this.state.data?.surveys.length > 0 && (
                        <FilterButton
                            onClearClick={() => {
                                this.setState({
                                    targetGroupId: undefined,
                                    questionId: undefined,
                                    surveyId: undefined
                                }, this.fetch);
                            }}
                            hasFilter={!!this.state.questionId || !!this.state.targetGroupId || !!this.state.surveyId}
                            renderContent={() => {
                                return (
                                    <div>
                                        <FormSection>
                                            <FormControl>
                                                <Dropdown
                                                    defaultSelectedKey={0}
                                                    label={localize("Malgrupp")}
                                                    selectedKey={this.state.targetGroupId}
                                                    onChange={(e, v) => {
                                                        const id = Number(v.key);

                                                        this.setState({
                                                            targetGroupId: id
                                                        }, this.fetch);
                                                    }}
                                                    options={[
                                                        {
                                                            key: 0,
                                                            text: localize("Alla")
                                                        },
                                                        ...this.state.targetGroups.map((tg) => (
                                                            {
                                                                key: tg.id,
                                                                text: tg.name
                                                            }
                                                        ))
                                                    ]}
                                                />
                                            </FormControl>
                                            <FormControl>
                                                <Dropdown
                                                    defaultSelectedKey={0}
                                                    label={localize("SURVEY")}
                                                    selectedKey={this.state.surveyId}
                                                    onChange={(e, v) => {
                                                        const id = Number(v.key);

                                                        this.setState({
                                                            surveyId: id,
                                                        }, this.fetch);
                                                    }}
                                                    options={[
                                                        {
                                                            key: 0,
                                                            text: localize("Alla")
                                                        },
                                                        ...this.state.data.surveys.map((q) => ({
                                                            key: q.id,
                                                            text: moment(q.createdDate).format("ll"),
                                                        }))
                                                    ]}
                                                />
                                            </FormControl>
                                            <FormControl>
                                                <Dropdown
                                                    defaultSelectedKey={0}
                                                    label={localize("QUESTION")}
                                                    selectedKey={this.state.questionId}
                                                    onChange={(e, v) => {
                                                        const id = Number(v.key);

                                                        this.setState({
                                                            questionId: id,
                                                        }, this.fetch);
                                                    }}
                                                    options={[
                                                        {
                                                            key: 0,
                                                            text: localize("Alla")
                                                        },
                                                        ...this.state.data.questions.sort((a, b) => compare(a, b, "text", true)).map((q) => ({
                                                            key: q.id,
                                                            text: q.text,
                                                        }))
                                                    ]}
                                                    calloutProps={{
                                                        calloutWidth: 700
                                                    }}
                                                />
                                            </FormControl>
                                        </FormSection>
                                    </div>
                                )
                            }}
                        />
                    )}
                    {activeProject?.template === 2 && (
                        <DefaultButton onClick={() => {
                            this.setState({
                                popupFormState: {
                                    id: this.props.activeProjectId,
                                    title: localize("EDIT_PROJECT"),
                                    isDisplayed: true,
                                    hideHeader: true
                                }
                            });
                        }}>
                            {localize("EDIT_PROJECT")}
                        </DefaultButton>
                    )}
                    {activeProject?.template === 2 && (
                        <PrimaryButton
                            className="create-button"
                            onClick={() => {
                                this.setState({
                                    popupFormState: {
                                        id: 0,
                                        title: localize("START_NEW_PROJECT"),
                                        isDisplayed: true,
                                        hideHeader: true
                                    }
                                });
                            }}>
                            <Visage2Icon icon="add" />
                            {localize("START_NEW_PROJECT")}
                        </PrimaryButton>
                    )}
                    <Notifications />
                </div>
            </div>
        )
    }

    renderGoalForm() {
        return (
            <FormPopup
                popupFormState={this.state.goalPopupFormState}
                onClose={() => {
                    this.setState({
                        goalPopupFormState: undefined
                    });
                    
                    this.setState({
                        goalPopupFormState: undefined
                    });
                }}>
                <GoalForm
                    value={this.state.data.target}
                    onSaveClick={(value: number) => {
                        api.put("/api/v1/surveys/goal/" + this.props.activeVectorId, value, {
                            headers: {
                                "Content-Type": "application/json",
                            },
                        });

                        this.setState({
                            data: {
                                ...this.state.data,
                                target: value
                            }
                        });

                        this.setState({
                            goalPopupFormState: undefined
                        });
                    }}
                    onCancelClick={() => {
                        onFormClose(this.props.dispatch, () => {
                            this.setState({
                                goalPopupFormState: undefined
                            });
                        });
                    }} />
            </FormPopup>
        )
    }
    
    renderFormPopup() {
        return (
            <FormPopup
                popupFormState={this.state.popupFormState}
                onClose={() => {
                    this.setState({
                        popupFormState: undefined
                    });
                }}
            >
                <ProjectForm
                    id={this.state.popupFormState?.id}
                    onClose={() => {
                        this.setState({
                            popupFormState: undefined
                        });
                    }} />
            </FormPopup>
        )
    }

    public renderContent() {
        if (this.state.isLoading) {
            return <Loader />;
        }

        if (this.state.errors.length > 0) {
            return (
                <>
                    {this.state.errors.map((error: string, index: number) => {
                        return (
                            <StatusMessageWithIcon text={localize(error)} icon="home-1" key={index} />
                        )
                    })}
                </>
            )
        }

        return (
            <div className="boxed-content">
                {this.state.data.surveys.length > 0 && (
                    <div className="row">
                        <div className="column chart-column">
                            {this.renderChart()}
                            <ChartFooterButtons
                                leftButtons={[{
                                    text: localize("EDIT_GOAL"),
                                    icon: "trend-up",
                                    onClick: () => {
                                        this.setState({
                                            goalPopupFormState: {
                                                id: 0,
                                                title: localize("ADD_GOAL"),
                                                isDisplayed: true
                                            }
                                        });
                                    }
                                }]}
                                rightButtons={[{
                                    text: localize("EXPORT_DATA"),
                                    icon: "export-1",
                                    tooltipText: localize("EXPORT_DATA_INFO"),
                                    onClick: () => {
                                        this.setState({
                                            displayExportPopup: true
                                        });
                                    }
                                }, {
                                    text: localize("CALCULATION_DESCRIPTION_LABEL"),
                                    icon: "info-circle",
                                    tooltipText: localize("CALCULATION_DESCRIPTION"),
                                }]}
                            />
                        </div>
                        <div className="column">
                            {this.renderSurveys()}
                        </div>
                    </div>
                )}
                <div className="row">
                    <div className="column">
                        {this.renderQuestions()}
                    </div>
                </div>
                {this.props.pro && (
                    <>
                        <div className="row">
                            <div className="column">
                                {this.renderActionList()}
                            </div>
                        </div>
                        {this.renderActionForm()}
                    </>
                )}
                {this.renderGoalForm()}
            </div>
        );
    }

    private stringToColor = (str) => {
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            hash = str.charCodeAt(i) + ((hash << 5) - hash);
        }
        let color = "#";
        for (let i = 0; i < 3; i++) {
            const value = (hash >> (i * 8)) & 0xff;
            color += ("00" + value.toString(16)).substr(-2);
        }
        return color;
    };

    getSurveyForChart() {
        const surveyLimit = 6;

        const surveyChartCount = this.state.data.surveys.length < surveyLimit ?
            this.state.data.surveys.length :
            surveyLimit;

        const surveys = [];

        for (let i = 0; i < surveyChartCount; i++) {
            surveys.push(this.state.data.surveys[i]);
        }

        return surveys;
    }

    getChartData() {
        const surveys = this.getSurveyForChart();

        return {
            labels: [
                "",
                ...surveys.map((s) =>
                    moment(s.createdDate).format("L")
                ),
                ""
            ],
            datasets: [
                {
                    borderColor: Style.getHexFromSpintrColor("p-green-2"),
                    tension: 0.5,
                    data: [
                        surveys[0]?.value || 0,
                        ...surveys.map((s) => s.value),
                        surveys[surveys.length - 1]?.value || 0,
                    ],
                    pointBorderWidth: 0,
                    pointBorderColor: 'transparent',
                    pointRadius: 6,
                    pointHoverRadius: 7,
                    pointBackgroundColor: [
                        "transparent",
                        ...surveys.map((s) => s.color),
                        "transparent",
                    ],
                    gradient: {
                      borderColor: {
                        axis: 'x',
                        colors: {
                          0: '#00A57815',
                          100: '#00A578',
                        },
                      },
                    },
                },
            ],
        }
    }

    getChartOptions() {
        return LineChartOptions(
            this.state.data.target || 0,
            100,
            (x: number, y: number) => {
                if (this.state.targetX === x &&
                    this.state.targetY === y) {
                    return;
                }

                this.setState({
                    targetX: x,
                    targetY: y
                });
            },
        );
    }

    renderChart() {
        return (
            <div className="chart-wrapper">
                <Line
                    // @ts-ignore
                    ref={this.chartRef}
                    // @ts-ignore
                    data={this.getChartData()}
                    // @ts-ignore
                    options={this.getChartOptions()}
                />
                {this.state.data.target > 0 && (
                    <div
                        className="goal-label"
                        style={{
                            top: this.state.targetY,
                            left: this.state.targetX,
                        }}>
                        <Label size="body-3" color="p-white" fontType="display">{localize("GOAL")}</Label>
                    </div>
                )}
            </div>
        )
    }

    renderQuestions() {
        return (
            <div>
                <div className="content-header">
                    <Label fontType="display" size="h4" weight="medium">
                        {localize("QUESTIONS")}
                    </Label>
                </div>
                <div className="content-list-wrapper">
                    <SpintrList
                        ref={this.questionsListRef}
                        fetch={(skip, take, columnId, isAscending, searchQuery) => {
                            return new Promise((resolve, reject) => {
                                let items = [...this.state.data.questions].sort((a, b) => compare(a, b, columnId, isAscending));

                                if (this.state.questionId) {
                                    items = items.filter(x => x.id === this.state.questionId);
                                }

                                resolve({
                                    data: items,
                                    totalCount: items.length
                                });
                            });
                        }}
                        take={1000}
                        orderByColumn={"text"}
                        columns={[
                            {
                                name: localize("QUESTION"),
                                fieldName: "text"
                            },
                            {
                                name: localize("AntalSvar"),
                                fieldName: "userAnswerCount",
                                minWidth: 75,
                                onRender: (item) => {
                                    return (
                                        <span>
                                            {item.userAnswerCount}
                                        </span>
                                    );
                                },
                            },
                            {
                                name: localize("SCORE"),
                                fieldName: "answer",
                                minWidth: 75,
                                onRender: (item) => {
                                    if (!item.userAnswerCount) {
                                        return null;
                                    }

                                    return (
                                        <span>
                                            {item.answer}
                                        </span>
                                    );
                                },
                            },
                        ]}
                        disableCommandBar
                    />
                </div>
            </div>
        )
    }

    renderSurveys() {
        if (this.state.data.surveys.length === 0) {
            return (
                <StatusMessageWithIcon text={localize("NO_DATA_AVAILABLE")} icon="home-1" noSpacing />
            )
        }

        return (
            <SpintrList
                ref={this.surveysListRef}
                fetch={(skip, take, columnId, isAscending, searchQuery) => {
                    return new Promise((resolve, reject) => {
                        const compare = (a, b) => {
                            const aVal = a[columnId];
                            const bVal = b[columnId];

                            if (isAscending) {
                                return aVal > bVal ? 1 : -1;
                            } else {
                                return aVal < bVal ? 1 : -1;
                            }
                        }

                        const items = [...this.state.data.surveys].sort(compare);

                        resolve({
                            data: items,
                            totalCount: this.state.data.surveys.length
                        });
                    });
                }}
                take={1000}
                isDescending
                orderByColumn={"createdDate"}
                columns={[
                    {
                        name: localize("SURVEY"),
                        fieldName: "createdDate",
                        onRender: (item) => {
                            return (
                                <div className="survey-name">
                                    <div className="color-ball" style={{
                                        backgroundColor: item.color
                                    }} />
                                    <div>
                                        {moment(item.createdDate).format("ll")}
                                    </div>
                                </div>
                            );
                        },
                    },
                    {
                        name: localize("AntalSvar"),
                        fieldName: "userAnswerCount",
                        minWidth: 75,
                        onRender: (item) => {
                            return (
                                <span>
                                    {item.userAnswerCount}/{item.userTotalCount}
                                </span>
                            );
                        },
                    },
                    {
                        name: localize("SCORE"),
                        fieldName: "value",
                        minWidth: 75,
                        onRender: (item) => {
                            if (!item.userAnswerCount) {
                                return null;
                            }

                            return (
                                <span>
                                    {item.value}
                                </span>
                            );
                        },
                    },
                    {
                        name: capitalizeFirstLetter(localize("COMMENTS")),
                        fieldName: "comments",
                        onRender: (item) => {
                            const id = "comments-" + item.id;
                            const isEmpty = item.comments.length === 0;

                            return (
                                <div>
                                    <UnstyledButton
                                        disabled={isEmpty}
                                        onClick={() => {
                                            this.setState({
                                                showCommentsCalloutForSurveyId: item.id
                                            });
                                        }}
                                        className="survey-comments-button"
                                        id={id}>
                                        <Visage2Icon icon="message" color={isEmpty ? "p-dark-grey-1" : "p-link-color"} size="small" />
                                        <Label color={isEmpty ? "p-dark-grey-1" : "p-link-color"} fontType="display" size="body-2">{item.comments.length}</Label>
                                    </UnstyledButton>
                                    {this.state.showCommentsCalloutForSurveyId === item.id && (
                                        <Callout
                                            className="Phoenix-callout wide"
                                            isBeakVisible={false}
                                            role="dialog"
                                            gapSpace={0}
                                            target={"#" + id}
                                            onDismiss={() => {
                                                this.setState({
                                                    showCommentsCalloutForSurveyId: 0
                                                });
                                            }}
                                            setInitialFocus
                                        >
                                            <div className="Phoenix-callout-header">
                                                <Label weight="medium" fontType="display">{capitalizeFirstLetter(localize("COMMENTS"))}</Label>
                                            </div>
                                            <div className="Comments-list">
                                                {item.comments.map((c: any, index:number) => {
                                                    return (
                                                        <div className="Comment-wrapper" key={c.id}>
                                                            <Label fontType="display" size="body-3" color="p-mid-grey-1">{localize("QUESTION") + ": " + c.question.text}</Label>
                                                            <div className="Comment">
                                                                <SpintrUser
                                                                    imageUrl={c.user ? c.user.imageUrl : ""}
                                                                    big
                                                                    name={c.user ? c.user.name : localize("ANONYMOUS")}
                                                                    hideText
                                                                />
                                                                <div className="Comment-right">
                                                                    <div className="top-row">
                                                                        <Label fontType="display" weight="medium" size="body-2">
                                                                            {c.user ? c.user.name : localize("ANONYMOUS_USER")}
                                                                        </Label>
                                                                        <Label fontType="display" weight="medium" color="p-mid-grey-1" size="body-3">
                                                                            {moment(c.createdDate).format("ll")}
                                                                        </Label>
                                                                    </div>
                                                                    <Label fontType="display" size="body-2" color="p-mid-grey-1">
                                                                        {c.text}
                                                                    </Label>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                        </Callout>
                                    )}
                                </div>
                            )
                        }
                    },
                    // {
                    //     name: "",
                    //     minWidth: listActionMenuWidth,
                    //     maxWidth: listActionMenuWidth,
                    //     onRender: (item: any) => {
                    //         return (
                    //             <ActionMenu
                    //                 categories={[
                    //                     {
                    //                         items: [{
                    //                             text: localize("Redigera"),
                    //                             onClick: () => {
                                                    
                    //                             }
                    //                         }, {
                    //                             text: item.deleted ? localize("Aterstall") : localize("TaBort"),
                    //                             onClick: () => {

                    //                             },
                    //                         }],
                    //                     },
                    //                 ]}
                    //             />
                    //         );
                    //     },
                    // },
                ]}
                disableCommandBar
            />
        )
    }

    hideActionItemPopupForm() {
        onFormClose(this.props.dispatch, () => {
            this.setState({
                selectedActionItemId: 0,
                actionPopupFormState: undefined
            });
        });
    }

    renderActionForm() {
        const selectedAction = this.state.data.actionItems.find(x => x.id === this.state.selectedActionItemId);

        return (
            <FormPopup
                popupFormState={this.state.actionPopupFormState}
                onClose={this.hideActionItemPopupForm.bind(this)}>
                <ActionItemForm
                    item={selectedAction || {
                        priority: 0,
                        status: 1,
                        vectorId: this.props.activeVectorId,
                        dueDate: new Date()
                    }}
                    onDelete={() => {
                        this.fetch();
                        this.hideActionItemPopupForm();
                    }}
                    onSave={() => {
                        this.fetch();
                        this.hideActionItemPopupForm();
                    }}
                    onCancelClick={this.hideActionItemPopupForm.bind(this)} />
            </FormPopup>
        )
    }

    renderActionList() {
        return (
            <div>
                <div className="content-header">
                    <Label fontType="display" size="h4" weight="medium">
                        {localize("AREAS_FOCUS")}
                    </Label>
                    <PrimaryButton
                        onClick={() => {
                            this.setState({
                                selectedActionItemId: 0,
                                actionPopupFormState: {
                                    id: 0,
                                    title: localize("ADD_ACTION"),
                                    isDisplayed: true
                                }
                            });
                        }}
                        className="create-button">
                        <Visage2Icon icon="add" />
                        {localize("ADD_ACTION")}
                    </PrimaryButton>
                </div>
                <div className="content-list-wrapper">
                    {this.state.data.actionItems.length === 0 && (
                        <StatusMessageWithIcon text={localize("NO_DATA_AVAILABLE")} icon="home-1" noSpacing />
                    )}
                    {this.state.data.actionItems.length > 0 && (
                        <SpintrList
                            ref={this.actionItemsListRef}
                            fetch={(skip, take, columnId, isAscending, searchQuery) => {
                                return new Promise((resolve, reject) => {
                                    const compare = (a, b) => {
                                        const aVal = a[columnId];
                                        const bVal = b[columnId];

                                        if (isAscending) {
                                            return aVal > bVal ? 1 : -1;
                                        } else {
                                            return aVal < bVal ? 1 : -1;
                                        }
                                    }

                                    const items = [...this.state.data.actionItems].sort(compare);

                                    resolve({
                                        data: items,
                                        totalCount: this.state.data.actionItems.length
                                    });
                                });
                            }}
                            take={1000}
                            isDescending
                            orderByColumn={"priority"}
                            columns={[
                                {
                                    name: localize("ACTION"),
                                    fieldName: "text",
                                },
                                {
                                    name: localize("PRIORITY"),
                                    fieldName: "priority",
                                    minWidth: 100,
                                    onRender: (item) => {
                                        return (
                                            <LabelBox text={localize("PRIORITY_" + item.priority)} prio={item.priority} />
                                        )
                                    }
                                },
                                {
                                    name: localize("DUE_DATE"),
                                    fieldName: "dueDate",
                                    minWidth: 150,
                                    onRender: (item) => {
                                        return (
                                            <span>{moment(item.dueDate).format("ll")}</span>
                                        )
                                    }
                                },
                                {
                                    name: localize("ASSIGNEE"),
                                    fieldName: "assigneeName",
                                    minWidth: 150,
                                    onRender: (item) => {
                                        if (!item.assignee) {
                                            return null;
                                        }

                                        return (
                                            <SpintrUser name={item.assignee.name} imageUrl={item.assignee.imageUrl} />
                                        );
                                    }
                                },
                                {
                                    name: localize("STATUS"),
                                    fieldName: "status",
                                    minWidth: 100,
                                    onRender: (item) => {
                                        return <span>{localize("ACTION_ITEM_STATUS_" + item.status)}</span>;
                                    }
                                },
                                {
                                    name: "",
                                    minWidth: listActionMenuWidth,
                                    maxWidth: listActionMenuWidth,
                                    onRender: (item: any) => {
                                        return (
                                            <ActionMenu
                                                categories={[
                                                    {
                                                        items: [{
                                                            text: localize("Redigera"),
                                                            onClick: () => {
                                                                this.setState({
                                                                    selectedActionItemId: item.id,
                                                                    popupFormState: {
                                                                        id: item.id,
                                                                        title: localize("ADD_ACTION"),
                                                                        isDisplayed: true
                                                                    }
                                                                });
                                                            }
                                                        }, {
                                                            text: item.deleted ? localize("Aterstall") : localize("TaBort"),
                                                            onClick: () => {
                                                                this.props.dispatch(
                                                                    setConfirmPopup({
                                                                        isOpen: true,
                                                                        title: localize("TaBort"),
                                                                        message: localize("ArDuSakerPaAttDuVillTaBortDennaPost"),
                                                                        onConfirm: async () => {
                                                                            api.delete("/api/v1/plans/action/" + item.id).then(() => {
                                                                                this.fetch();
                                                                            }).catch(() => {});
                                                                        },
                                                                    })
                                                                );
                                                            },
                                                        }],
                                                    },
                                                ]}
                                            />
                                        );
                                    },
                                },
                            ]}
                            disableCommandBar
                        />
                    )}
                </div>
            </div>
        )
    }
}

export const LineChartOptions = (
    goal: number,
    max: number,
    onInit: (_x: number, _y: number) => void,
): ChartOptions<'line'> => ({
    responsive: true,
    maintainAspectRatio: false,
    scales: {
        y: {
            min: 0,
            max: max,
            ticks: {
                count: 5,
                color: Style.getHexFromSpintrColor("p-mid-grey-1"),
                font: {
                    size: 12,
                    family: 'EloquiaDisplay Regular',
                },
                callback: (scale: any) => {
                    return Math.round(scale)
                },
                padding: 8,
                autoSkip: true,
            },
            beginAtZero: true,
            grid: {
                display: true,
                color: Style.getHexFromSpintrColor("p-border-color"),
                lineWidth: 0.5,
                drawTicks: false,
                borderColor: Style.getHexFromSpintrColor("p-border-color"),
                borderWidth: 0.5
            },
        },
        x: {
            ticks: {
                color: Style.getHexFromSpintrColor("p-mid-grey-1"),
                font: {
                    size: 12,
                    family: 'EloquiaDisplay Regular',
                },
                padding: 8
            },
            grid: {
                display: true,
                color: Style.getHexFromSpintrColor("p-border-color"),
                lineWidth: 0.5,
                drawTicks: false,
                tickLength: 0,
                borderColor: Style.getHexFromSpintrColor("p-border-color"),
                borderWidth: 0.5
            },
        },
    },
    plugins: {
        tooltip: {
            enabled: true,
            titleFont: {
                size: 12,
                family: 'EloquiaDisplay Regular',
            },
            bodyFont: {
                size: 12,
                family: 'EloquiaDisplay Regular',
            },
            displayColors: false,
            callbacks: {
                label: (tooltipItem) => {
                    let title = tooltipItem.label;

                    // for (let item of this.props.data) {
                    //     if (item.name == tooltipItem.label &&
                    //         !!item.title) {
                    //         title = item.title;
                    //     }
                    // }

                    return title + ": " + tooltipItem.formattedValue;
                },
                title: () => "",
            },
        },
        legend: {
            display: false,
        },
        annotation: {
            annotations: {
            target: {
                display: !!goal,
                type: 'line',
                drawTime: 'afterDatasetsDraw',
                borderDash: [3],
                borderWidth: 1,
                borderColor: '#787CDD',
                yMin: goal,
                yMax: goal,
                init: ({ properties }: any) => {
                    if (properties) {
                        onInit(properties.x, properties.y)
                    }
                },
            },
            },
        },
    },
  })  

const mapStateToProps = (state: IApplicationState, props) => ({
    ...props,
    hasCustomBodyFont: !!state.instance.currentInstance.bodyFontUrl,
    instanceColor: state.instance.currentInstance.color,
    pro: state.instance.currentInstance.pro,
    isAdmin: state.user.currentUser.isAdmin,
    activeProjectId: state.project.activeProjectId,
    activeVectorId: state.project.activeVectorId,
    projects: state.project.projects,
});

export default connect(mapStateToProps)(DetailsView);
